import i18next from 'i18next';
import { createSelector } from 'reselect';
import { getLocalID } from '../local';
import { MEDIA_TYPE } from '../media/constants';
import { toState } from '../redux';
import { getTracksByTrackIdAndMediaType } from '../tracks/functions';
import { MEMBER_STATUS, MEMBER_STATUS_LEVEL, ROLE, ROLE_LEVEL } from './constans';
/**
 * 표시용 닉네임. 진행자(manager)는 전 영역(채팅·참석자 목록·영상 타일·듀얼모니터 등)에서
 * 실명 대신 '조교'(role.assistant)로 표기한다(2026-07-27 요구).
 * 사용자 이름 문자열을 화면에 렌더하는 곳은 반드시 이 함수를 거칠 것.
 * ※예외: 영상 워터마크는 "시청자 본인" 식별용(유출 추적)이라 마스킹 대상이 아니다.
 */
export function getDisplayNickname(nickname, role) {
    if (role === ROLE.MANAGER)
        return i18next.t('role.assistant');
    return nickname || '';
}
export function isLocalParticipant(stateful) {
    const localRole = getLocalMemberRole(stateful) || ROLE.PARTICIPANT;
    return ![ROLE.ADMIN, ROLE.HOST, ROLE.MANAGER].includes(localRole);
}
/**
 *
 */
export function getRoleMenu(local_role, remote_role) {
    switch (local_role) {
        case ROLE.ADMIN:
        case ROLE.HOST:
        case ROLE.MANAGER:
            if (checkRoleLevel(local_role, remote_role)) {
                return ['presenter', 'handler', 'mic', 'voice', 'pin', 'etc'];
            }
    }
}
/**
 * 모든 사용자 가져오기 + filter
 * @param {Function} stateful
 * @param {Array} status
 * @returns
 */
export function getMemberList(stateful, data, isSort = true) {
    const state = toState(stateful);
    const { local, remote } = state['features/base/members'];
    const list = [local, ...remote];
    const status = data?.status || null;
    const excludePin = data?.excludePin || false;
    const excludeLocal = data?.excludeLocal || false;
    const excludeVoice = data?.excludeVoice || false;
    const excludeIds = data?.excludeIds || null;
    const nickname = data?.nickname || null;
    const excludeHost = data?.excludeHost || null;
    const excludeManager = data?.excludeManager || null;
    const voice = getVoiceMember(state);
    const members = [];
    for (let i = 0; i < list.length; i++) {
        const uuid = list[i];
        if (!uuid)
            continue;
        const user = getMemberTrackByUserID(state, uuid, true);
        if (!user)
            continue;
        const { status: userStatus, local: userLocal, user_uuid: userUUID, pinned: userPinned, role: userRole, nickname: userNickname, } = user;
        const role_name = userRole;
        if ((status ? status.includes(userStatus) : true) &&
            (excludeLocal ? !userLocal : true) &&
            (excludeVoice ? voice !== userUUID : true) &&
            (excludePin ? !userPinned : true) &&
            (excludeHost ? role_name !== ROLE.HOST : true) &&
            (excludeIds ? !excludeIds.includes(userUUID) : true) &&
            (nickname && userNickname ? userNickname.includes(nickname) : true) &&
            (excludeManager ? !checkRoleLevel(role_name, ROLE.PRESENTER) : true)) {
            members.push(user);
        }
    }
    if (isSort) {
        members.sort((a, b) => {
            const nicknameComparison = (a.nickname && b.nickname && a.nickname.localeCompare(b.nickname)) || 0;
            const localComparison = (b?.local === true ? 1 : 0) - (a?.local === true ? 1 : 0);
            const pinnedComparison = +b.pinned - +a.pinned;
            const voiceComparison = voice === a.user_uuid ? -1 : 0;
            const roleComparison = ROLE_LEVEL.indexOf(a.role) - ROLE_LEVEL.indexOf(b.role);
            const statusComparison = MEMBER_STATUS_LEVEL.indexOf(a.status) - MEMBER_STATUS_LEVEL.indexOf(b.status);
            if (localComparison !== 0) {
                return localComparison;
            }
            if (statusComparison !== 0) {
                return statusComparison;
            }
            if (roleComparison !== 0) {
                return roleComparison;
            }
            if (nicknameComparison !== 0) {
                return nicknameComparison;
            }
            if (pinnedComparison !== 0) {
                return pinnedComparison;
            }
            if (voiceComparison !== 0) {
                return voiceComparison;
            }
        });
    }
    return members;
}
export function getOccupiedRemoteBridgeId(stateful, excludeLocal) {
    const state = toState(stateful);
    // 읽기 전용 순회 — 딥클론 불필요(과거 cloneDeep 이 모든 구독 재평가마다 O(N) 비용을 부과했음)
    const members = state['features/base/members'].members;
    const remote = [...members.values()]
        .filter(i => i.status === 'occupied' && (excludeLocal ? !i?.local : true))
        .map(i => {
        return getMemberByBridgeId(stateful, i.user_uuid);
    });
    return remote;
}
export function getRemoteId(stateful, excludeIds = [], sortVacatedLast) {
    const state = toState(stateful);
    // 읽기 전용 순회 — 딥클론 불필요
    const members = state['features/base/members'].members;
    let remote = [];
    // 닉네임 미설정 멤버가 있어도 정렬이 throw 하지 않도록 방어
    const byNickname = (a, b) => (a.nickname || '').localeCompare(b.nickname || '');
    if (sortVacatedLast) {
        // [레이아웃 정렬] — 상태 기반 분류.
        // (과거에는 join_time 과 클릭 시각을 비교했으나 join_time 을 클라이언트가 채우지 않아
        // 전원이 같은 그룹으로 분류돼 정렬이 항상 no-op 이었음. 시간 비교를 제거하고
        // "참여 중(occupied) 닉네임순 → 이탈(vacated 등) 닉네임순 뒤로"의 순수 상태 기반으로 교체)
        const filteredMembers = [...members.values()].filter(i => !excludeIds.includes(i.user_uuid) && !checkRoleLevel(i.role, ROLE.PRESENTER));
        const occupied = filteredMembers.filter(i => i.status === MEMBER_STATUS.OCCUPIDE).sort(byNickname);
        const others = filteredMembers.filter(i => i.status !== MEMBER_STATUS.OCCUPIDE).sort(byNickname);
        remote = [...occupied, ...others].map(i => i.user_uuid);
    }
    else {
        // 기본: 단순 닉네임 순 정렬 (기존 동작 유지)
        remote = [...members.values()]
            .filter(i => !excludeIds.includes(i.user_uuid) && !checkRoleLevel(i.role, ROLE.PRESENTER))
            .sort(byNickname)
            .map(i => i.user_uuid);
    }
    return remote;
}
export function getMembersByMember(members, isAll = false) {
    if (isAll)
        return (members
            .filter(member => member.hands_up || (member.pinned && member.role === ROLE.PARTICIPANT))
            // @ts-ignore
            .sort((a, b) => a?.hands_up_time - b?.hands_up_time));
    return members
        .filter(member => member.hands_up || (member.pinned && member.role === ROLE.PARTICIPANT))
        .map(member => member.user_uuid);
}
export function getHandlerRequestMember(stateful, isAll = false) {
    const members = getMemberList(stateful, {
        status: [MEMBER_STATUS.OCCUPIDE],
    }, false);
    const filteredAndSortedMembers = members
        .filter(member => member.hands_up || (member.pinned && member.role === ROLE.PARTICIPANT))
        .sort((a, b) => {
        // 1. pinned 여부 우선 정렬
        if (b.pinned !== a.pinned) {
            return +b.pinned - +a.pinned; // pinned 여부 우선 정렬
        }
        const timeA = a.hands_up_time ? new Date(a.hands_up_time).getTime() : 0;
        const timeB = b.hands_up_time ? new Date(b.hands_up_time).getTime() : 0;
        return timeA - timeB;
    });
    if (isAll) {
        return filteredAndSortedMembers;
    }
    return filteredAndSortedMembers.map(member => member.user_uuid);
}
export const getHandlerMemberMemoized = createSelector([
    (state) => state,
    (_state, flag) => flag
], (state, flag) => getHandlerRequestMember(state, flag));
export function getHandlerMember(stateful, isAll = false) {
    const pinList = toState(stateful)['features/base/members'].pinIds;
    const members = getMemberList(stateful, {
        status: [MEMBER_STATUS.OCCUPIDE],
        filterGroup: true,
    }, false).filter(member => pinList.has(member.user_uuid) && member.role === ROLE.PARTICIPANT);
    if (isAll)
        return members;
    else
        return members.map(member => member.user_uuid);
}
export function getMemberIdAndNicknameList(stateful, data, isSort = true) {
    const members = getMemberList(stateful, data, isSort);
    return members.map(t => {
        // 진행자 실명 마스킹 — 채팅 대상 선택 목록/선택 칩에 표시되는 이름
        return { user_uuid: t.user_uuid, nickname: getDisplayNickname(t.nickname, t.role) };
    });
}
export function getMemberIdsList(stateful, data, isSort = true) {
    const members = getMemberList(stateful, data, isSort);
    return members
        .sort((a, b) => {
        a.nickname && b.nickname && a.nickname.localeCompare(b.nickname);
    })
        .map(t => t.user_uuid);
}
/**
 * 현재 입장 중인 사용자 수 가져오기
 * @param {*} stateful
 * @returns
 */
export function getMemberCount(stateful) {
    const members = getMemberList(stateful, {
        status: [MEMBER_STATUS.OCCUPIDE],
        filterGroup: true,
    }, false);
    return members.length;
}
/**
 * 음성 사용자 가져오기
 * @param {Function} stateful
 * @returns
 */
export function getVoiceMember(stateful) {
    const state = toState(stateful);
    return state['features/base/members'].voice;
}
/**
 * 로컬 사용자 정보 가져오기
 * @param {*} stateful
 * @returns
 */
export function getLocalMember(stateful) {
    const state = toState(stateful);
    const local_uuid = state['features/base/members'].local;
    const local = getMemberByUserID(stateful, local_uuid);
    if (!local)
        return null;
    return local;
}
/**
 * 로컬 사용자의 권한 정보 가져오기
 * @param {*} stateful
 * @returns
 */
export function getLocalMemberRole(stateful) {
    const localID = getLocalID(stateful);
    if (!localID)
        return null;
    const local = getMemberTrackByUserID(stateful, localID, false);
    if (local) {
        return local.active_group === '' ? local.role : local.group_role;
    }
    return null;
}
/**
 * 모든 사용자 ID 조회(방에 없는 사용자 까지)
 * @param {Function} stateful
 * @param {string} user_uuid
 * @returns
 */
export function getMemberByUserID(stateful, user_uuid) {
    const state = toState(stateful);
    const members = state['features/base/members'].members;
    return user_uuid ? members.get(user_uuid) : null;
}
/**
 * 현재 권한 확인
 * @param {Function} stateful
 * @param {string} user_uuid
 */
export function getRoleByUserID(stateful, user_uuid) {
    const members = getMemberByUserID(stateful, user_uuid);
    return members && members.role;
}
/**
 * 사용자 member ID로 user ID 가져오기
 */
export function getUserIDByMemberId(stateful, member_uuid) {
    const state = toState(stateful);
    const memberIDs = state['features/base/members']?.memberIds;
    if (!member_uuid)
        return null;
    return memberIDs && memberIDs.get(member_uuid);
}
/**
 * 사용자 아이디로 사용자 트랙 아이디 가져오기
 * @param {Function} stateful
 * @param {string} user_uuid
 * @returns
 */
export function getBridgeSession(stateful, user_uuid) {
    const state = toState(stateful);
    const bridgeList = state['features/base/members'].bridgeList;
    return bridgeList.get(user_uuid);
}
export function getMemberByBridgeId(stateful, user_uuid) {
    const data = getBridgeSession(stateful, user_uuid);
    return data?.id;
}
export function getLoaclMemberTrack(stateful) {
    const localID = getLocalID(stateful);
    if (!localID)
        return undefined;
    const member = getMemberTrackByUserID(stateful, localID);
    return member;
}
/**
 * 사용자 아이디로 트랙 정보 가져오기
 * @param {Function} stateful
 * @param {string} user_uuid
 */
export function getMemberTrackByUserID(stateful, user_uuid, isTrack = true) {
    const state = toState(stateful);
    const member = getMemberByUserID(state, user_uuid);
    if (!member)
        return null;
    if (isTrack) {
        const jitsi_id = getMemberByBridgeId(state, user_uuid);
        const videoTrack = jitsi_id && getTracksByTrackIdAndMediaType(state, member.local ? 'local' : jitsi_id, MEDIA_TYPE.VIDEO);
        const audioTrack = jitsi_id && getTracksByTrackIdAndMediaType(state, member.local ? 'local' : jitsi_id, MEDIA_TYPE.AUDIO);
        return {
            ...member,
            videoTrack,
            audioTrack,
        };
    }
    else {
        const bridge_sessions = getBridgeSession(state, user_uuid);
        return {
            ...member,
            videoMuted: bridge_sessions?.video_muted,
            audioMuted: bridge_sessions?.audio_muted,
        };
    }
}
/**
 * local 사용자 아이디 가져오기
 * @param {Function} stateful
 * @returns
 */
export function getLocalIDMember(stateful) {
    const state = toState(stateful);
    return state['features/base/members'].local;
}
/**
 * 권한 레벨 확인
 * @param {string} localRole
 * @param {string} memberRole
 * @returns
 */
export function checkRoleLevel(localRole, memberRole) {
    if (!localRole || !memberRole)
        return false;
    if (ROLE_LEVEL.indexOf(localRole) >= ROLE_LEVEL.indexOf(memberRole))
        return false;
    return true;
}
/**
 * 사용자 핀 가져오기
 * @param {IStore['getState']} stateful
 * @param {String|null} excludeId
 * @returns
 */
export function getPinnedMember(stateful, excludeId, excludeLocal = false) {
    const state = toState(stateful);
    const pinList = state['features/base/members'].pinIds;
    // 원소가 문자열(uuid)이라 얕은 복사로 충분 — 딥클론 불필요
    const copyPin = new Set(pinList);
    excludeId && copyPin.delete(excludeId);
    excludeLocal && copyPin.delete(getLocalIDMember(stateful));
    return copyPin;
}
/**
 * 사용자 핀 개수 가져오기
 * @param {IStore['getState']} stateful
 * @param {String|null} excludeId
 * @returns
 */
export function getPinnedMemberCount(stateful, excludeId) {
    const pin = getPinnedMember(stateful, excludeId);
    return pin.size;
}
/**
 * 핀 사용자 가져오기
 * @param {IStore['getState']} stateful
 * @param {Boolean} isSeminar
 * @returns {string[]}
 */
export function getPinnedMembersBySeminar(stateful) {
    const state = toState(stateful);
    const pin = getPinnedMember(state);
    return Array.from(pin);
}
/**
 * 그룹 아이디로 사용자 검색
 * @param {*} stateful
 * @param {string} group_id
 * @returns
 */
export function getGroupMemberByGroupId(stateful, group_id) {
    const state = toState(stateful);
    const members = state['features/base/members'].members;
    return group_id ? [...members.values()].filter(member => member.group_id === group_id) : [members.values()];
}
/**
 * 입장된 사용자 그룹 아이디로 사용자 검색
 * @param {*} stateful
 * @param {string} group_id
 * @returns
 */
export function getOccupiedMemberByGroupId(stateful, group_id) {
    const state = toState(stateful);
    const members = state['features/base/members'].members;
    return [...members.values()].filter(member => member.group_id === group_id && member.status === MEMBER_STATUS.OCCUPIDE && member.group_type === '');
}
/**
 * 녹화 사용자 가져오기
 * @param {*} stateful
 * @returns
 */
export function getMemberRecordList(stateful) {
    const state = toState(stateful);
    const list = state['features/base/members'].recordIds;
    return list && list.size > 0 ? [...list] : [];
}
/**
 * 현재 공유 중인 사용자 아이디 가져오기
 * @param {IStore['getState']} stateful
 * @returns
 */
export function getScreenUserID(stateful) {
    const state = toState(stateful);
    const share_member = state['features/screen-share'].share_member;
    if (share_member) {
        const member = getMemberByUserID(stateful, share_member);
        if (member)
            return share_member;
        else {
            const user_uuid = getUserIDByMemberId(stateful, share_member);
            return user_uuid;
        }
    }
    return null;
}
/**
 * 로컬 그룹 사용자 가져오기
 *
 * @param {IStore['getState']} stateful
 * @returns
 */
export function getLocalGroup(stateful) {
    const state = toState(stateful);
    const local = getLocalMember(state);
    const group = getGroupByUid(state, local?.group_id);
    return {
        role: local?.role,
        group_id: local?.group_id,
        group_role: local?.group_role,
        start: group?.start,
        group_name: group?.name,
    };
}
export async function getProcessedMember(member) {
    // 닉네임 없는 경우 무조건 닉네임 조회
    let nickname = member.nickname || '';
    let group_id = member.group_id || '';
    let group_role = member.group_role || '';
    let active_group = '';
    let group_type = '';
    const response = await APP.API.getRemoteUserInformation({ user_id: member.user_uuid });
    if (response.complete) {
        if (!nickname || nickname === '') {
            const item = response.data?.find((item) => item.nickname && item.nickname !== '');
            if (item)
                nickname = item.nickname;
        }
        // 그룹 조회
        // 새로 들어온 사용자
        const item = response.data?.find((item) => item.group_id !== '' && item.status !== MEMBER_STATUS.BANNED);
        if (item) {
            group_id = item.group_id;
            group_role = item.role.name;
            group_type = item.type;
            active_group = item.status === MEMBER_STATUS.OCCUPIDE ? item.group_id : '';
        }
    }
    return { ...member, nickname, group_role, group_id, group_type, active_group };
}
export function getSpeakerId(stateful) {
    const state = toState(stateful);
    const speakerId = state['features/base/members'].speakerId;
    const userId = getUserIDByMemberId(stateful, speakerId);
    if (userId) {
        const member = getMemberByUserID(stateful, userId);
        if (member?.status === MEMBER_STATUS.OCCUPIDE)
            return member.user_uuid;
    }
    // 발표자가 유효하지 않으면(퇴장 등 non-OCCUPIDE) 자기 자신(getLocalID) 대신 강사/호스트로 폴백.
    // (기존 self 폴백은 발표 중이던 학생 퇴장 시 seminar 큰 화면이 '자기 자신'이 되어 강사가
    //  seminar 에서 탈락 → 강사 타일·영상 구독이 함께 끊기는 버그의 원인이었음.)
    let host;
    const allMembers = state['features/base/members'].members;
    for (const m of allMembers.values()) {
        if (m.status !== MEMBER_STATUS.OCCUPIDE)
            continue;
        if (m.role !== ROLE.HOST && m.role !== ROLE.MANAGER)
            continue;
        // 상위 role 우선(HOST > MANAGER).
        if (!host || ROLE_LEVEL.indexOf(m.role) < ROLE_LEVEL.indexOf(host.role))
            host = m;
    }
    if (host)
        return host.user_uuid;
    // 호스트도 없으면 기존 동작(로컬) 유지.
    return getLocalID(stateful);
}
export function getAllMemberIds(stateful) {
    const state = toState(stateful);
    const members = state['features/base/members'].members;
    if (!members) {
        return [];
    }
    else {
        // 미리 인덱스를 계산하여 비교시 재계산하지 않도록 합니다.
        const statusLevelMap = new Map(MEMBER_STATUS_LEVEL.map((status, index) => [status, index]));
        const roleLevelMap = new Map(ROLE_LEVEL.map((role, index) => [role, index]));
        return Array.from(members.entries())
            .sort(([keyA, a], [keyB, b]) => {
            // 1. local 비교 (로컬 검색 1순위)
            if (a.local !== b.local) {
                return a.local ? -1 : 1;
            }
            // 2. status 비교 (상태 2순위)
            const statusA = statusLevelMap.get(a.status) || 0;
            const statusB = statusLevelMap.get(b.status) || 0;
            if (statusA !== statusB) {
                return statusB - statusA; // 높은 상태가 먼저 오도록 변경
            }
            // 3. role 비교 (권한 3순위)
            const roleA = roleLevelMap.get(a.role) || 0;
            const roleB = roleLevelMap.get(b.role) || 0;
            if (roleA !== roleB) {
                return roleB - roleA; // 높은 권한이 먼저 오도록 변경
            }
            // 4. nickname 비교 (닉네임 4순위)
            return (a.nickname || '').localeCompare(b.nickname || '');
        })
            .map(([key, _]) => key);
    }
}
